import { apiFetch } from './client';

// Attendance — grid view that lets a leader confirm who showed up at each
// activity for a team in a date range. Mirrors the server shape from
// internal/api/attendance.go.

export type AttendanceStatus = 'present' | 'absent' | 'excused';

export interface AttendanceMember {
  id: string;
  first_name: string;
  last_name: string;
  role: 'athlete' | 'leader';
  birth_year?: number | null;
}

export interface AttendanceActivity {
  id: string;
  title: string;
  starts_at: string; // RFC3339
  ends_at: string;   // RFC3339
  // Set once a leader/admin has confirmed (finalised) this activity's
  // attendance. While set, the column is locked — cell edits are rejected
  // (409 attendance_locked) until the activity is reopened.
  confirmed_at?: string | null;
}

export interface AttendanceRecord {
  activity_id: string;
  member_id: string;
  status: AttendanceStatus;
  notes?: string | null;
}

// AttendanceSubsidy carries the rule-set constants the SPA needs to render
// per-cell "tells" (eligible vs too-young vs too-old) and the column status
// pill. Absent on the response when the org's country has no rules
// configured — the SPA hides every subsidy-related visual in that case.
export interface AttendanceSubsidy {
  kind: string; // 'lok_se' | ''
  min_age: number;
  max_age: number;
  leader_min_age: number;
  min_participants: number;
  min_leaders: number;
  min_duration_minutes: number;
  max_participants_per_leader: number;
}

// AttendanceRollup is the per-activity verdict + counts the server pre-
// computes. One entry per activity column, in the same order as
// AttendanceGrid.activities. Empty/missing when no subsidy rules apply.
export interface AttendanceRollup {
  activity_id: string;
  present_count: number;
  absent_count: number;
  excused_count: number;
  unmarked_count: number;
  eligible_athletes_present: number;
  eligible_leaders_present: number;
  leaders_present: number;
  duration_minutes: number;
  qualifies: boolean;
  issues?: string[]; // e.g. ['too_short', 'no_leader']
}

export interface AttendanceGrid {
  team_id: string;
  from: string;
  to: string;
  activities: AttendanceActivity[];
  athletes: AttendanceMember[];
  leaders: AttendanceMember[];
  records: AttendanceRecord[];
  subsidy?: AttendanceSubsidy;
  rollups?: AttendanceRollup[];
}

const path = (slug: string, teamID: string, suffix: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}${suffix}`;

export async function getAttendanceGrid(
  token: string | null,
  slug: string,
  teamID: string,
  from: Date,
  to: Date,
): Promise<AttendanceGrid> {
  const qs = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  return apiFetch<AttendanceGrid>(
    `${path(slug, teamID, '/attendance-grid')}?${qs.toString()}`,
    { token },
  );
}

// Cell input for the confirm batch. Empty `status` clears the cell (soft-
// deletes any existing record). The server only persists cells that
// actually need to change — the SPA can send only the diff to keep the
// payload small on big grids.
export interface ConfirmAttendanceCell {
  activity_id: string;
  member_id: string;
  status: AttendanceStatus | '';
  notes?: string;
}

export interface ConfirmAttendanceResponse {
  changed: number;
}

export async function confirmAttendance(
  token: string | null,
  slug: string,
  teamID: string,
  cells: ConfirmAttendanceCell[],
): Promise<ConfirmAttendanceResponse> {
  return apiFetch<ConfirmAttendanceResponse>(path(slug, teamID, '/attendance'), {
    method: 'POST',
    body: { cells },
    token,
  });
}

// Activity-level confirmation (finalise/lock for subsidy reporting). Distinct
// from confirmAttendance above, which only saves cell marks. Confirming locks
// the column; reopening unlocks it for editing again.

export interface ConfirmActivityResponse {
  confirmed_at: string;
}

export async function confirmActivityAttendance(
  token: string | null,
  slug: string,
  teamID: string,
  activityID: string,
): Promise<ConfirmActivityResponse> {
  return apiFetch<ConfirmActivityResponse>(
    path(slug, teamID, `/activities/${encodeURIComponent(activityID)}/attendance-confirm`),
    { method: 'POST', token },
  );
}

export async function reopenActivityAttendance(
  token: string | null,
  slug: string,
  teamID: string,
  activityID: string,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    path(slug, teamID, `/activities/${encodeURIComponent(activityID)}/attendance-reopen`),
    { method: 'POST', token },
  );
}
